
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="h-20 flex items-center px-20">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/bf918e2e-2b57-4ffe-879c-52eeb698ecfc.png" 
              alt="MAX SRL Logo" 
              className="h-12 w-auto"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-light tracking-[0.2em] text-[#1a1a1a] uppercase">MAX</span>
              <span className="text-sm font-light tracking-[0.2em] text-[#1a1a1a] uppercase">SRL</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-20 min-h-screen flex items-center">
        <div className="max-w-[1600px] mx-auto w-full flex items-center gap-16">
          {/* Left Content Zone - 60% */}
          <div className="flex-[0_0_60%]">
            <h1 className="text-[72px] leading-[1.1] font-serif font-medium text-black mb-10 max-w-[520px]">
              Crafting Premium iOS Experiences for Modern Users
            </h1>
            <p className="text-xl font-light text-[#666666] max-w-[520px] leading-relaxed">
              Specialized in VPN security solutions and device optimization tools
            </p>
          </div>

          {/* Right Visual Element - 40% */}
          <div className="flex-[0_0_40%] flex justify-center items-center">
            <div className="relative w-full max-w-[480px] h-[600px]">
              <div 
                className="absolute inset-0 rounded-[32px] overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,0.08)]"
                style={{ transform: 'rotate(5deg)' }}
              >
                <div className="w-full h-full bg-gradient-to-br from-[#E8E4F3] via-[#E3F2ED] to-[#E8E4F3] flex items-center justify-center p-12">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/356d84bd1b8870105c947ee20f7a0897.jpg"
                    alt="iOS App Interface"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-[#FAFAF8] py-40 px-20">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex gap-20 items-start">
            {/* Left Content */}
            <div className="flex-[0_0_50%]">
              <div className="inline-block border border-black rounded-full px-4 py-1.5 mb-10">
                <span className="text-xs uppercase tracking-wider font-light">ABOUT MAX SRL</span>
              </div>
              
              <h2 className="text-[56px] leading-[1.2] font-serif font-medium text-black mb-12 max-w-[480px]">
                Expert Team Building Modern Applications
              </h2>
              
              <p className="text-lg font-light text-[#444444] leading-[1.8] max-w-[520px]">
                (MAX SRL) is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>

            {/* Right Visual */}
            <div className="flex-[0_0_50%]">
              <div className="rounded-[40px] overflow-hidden border border-black/[0.06] w-full h-[720px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20professional%20software%20development%20team%20workspace%20with%20multiple%20monitors%20showing%20iOS%20app%20code%20and%20design%20clean%20minimalist%20office%20interior%20natural%20lighting%20contemporary%20tech%20company%20environment%20collaborative%20atmosphere%20high%20quality%20professional%20photography&width=600&height=720&seq=about-team-workspace-001&orientation=portrait"
                  alt="MAX SRL Team Workspace"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="bg-white py-48 px-20">
        <div className="max-w-[1600px] mx-auto">
          {/* Section Header */}
          <div className="flex justify-between items-start mb-16">
            <h2 className="text-[64px] font-serif font-medium text-black">Our Products</h2>
            <p className="text-base font-light text-[#777777] max-w-[400px] leading-relaxed text-right">
              Innovative solutions designed to enhance security and optimize performance on iOS devices
            </p>
          </div>

          {/* Product Cards Grid */}
          <div className="grid grid-cols-3 gap-8">
            {/* VPN Product Card */}
            <div className="bg-[#0a0a0a] rounded-[36px] p-8 h-[420px] flex flex-col relative overflow-hidden">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-auto">
                <i className="ri-shield-check-line text-2xl text-black"></i>
              </div>
              
              <div className="mt-auto relative z-10">
                <h3 className="text-5xl font-sans text-white mb-4">VPN</h3>
                <p className="text-base text-white/70 leading-relaxed">
                  Secure connection and privacy protection for your mobile device
                </p>
              </div>

              {/* Abstract Network Visualization */}
              <div className="absolute bottom-0 right-0 w-64 h-64 opacity-20">
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600 to-blue-400 rounded-full blur-3xl"></div>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="bg-[#F8F6F3] rounded-[36px] p-8 h-[380px] flex flex-col">
              <div className="inline-flex items-center gap-2 bg-black/10 rounded-full px-3 py-1.5 self-start mb-6">
                <i className="ri-flashlight-line text-xs"></i>
                <span className="text-xs uppercase tracking-wide">OPTIMIZATION</span>
              </div>

              <div className="flex-1 flex items-center justify-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=minimalist%20clean%20storage%20optimization%20icon%20illustration%20showing%20organized%20files%20and%20folders%20simple%20geometric%20shapes%20soft%20cream%20background%20modern%20flat%20design%20professional%20app%20interface%20element&width=200&height=200&seq=cleaner-illustration-001&orientation=squarish"
                  alt="Cleaner Optimization"
                  className="w-40 h-40 object-contain"
                />
              </div>

              <div className="text-center">
                <h3 className="text-[42px] font-serif text-black mb-2">Cleaner</h3>
                <p className="text-[15px] text-[#666666]">Free up space and boost performance</p>
              </div>
            </div>

            {/* Feature Highlight Card */}
            <div className="bg-white rounded-[36px] p-8 h-[380px] flex flex-col shadow-[0_8px_24px_rgba(0,0,0,0.06)]">
              <div className="mb-8">
                <div className="text-[120px] font-serif text-black leading-none">2</div>
                <p className="text-lg text-[#666666] mt-2">Core Products</p>
              </div>

              <div className="grid grid-cols-2 gap-6 mt-auto">
                <div className="flex flex-col items-center justify-center p-6 bg-[#FAFAF8] rounded-2xl">
                  <i className="ri-lock-line text-3xl text-black mb-2"></i>
                  <span className="text-xs text-[#666666] text-center">Security</span>
                </div>
                <div className="flex flex-col items-center justify-center p-6 bg-[#FAFAF8] rounded-2xl">
                  <i className="ri-speed-up-line text-3xl text-black mb-2"></i>
                  <span className="text-xs text-[#666666] text-center">Performance</span>
                </div>
                <div className="flex flex-col items-center justify-center p-6 bg-[#FAFAF8] rounded-2xl">
                  <i className="ri-smartphone-line text-3xl text-black mb-2"></i>
                  <span className="text-xs text-[#666666] text-center">iOS Native</span>
                </div>
                <div className="flex flex-col items-center justify-center p-6 bg-[#FAFAF8] rounded-2xl">
                  <i className="ri-user-heart-line text-3xl text-black mb-2"></i>
                  <span className="text-xs text-[#666666] text-center">User Focus</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-[#1c1c1c] py-32 px-20">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex gap-20 items-start">
            {/* Left Visual Zone */}
            <div className="flex-[0_0_55%]">
              <div className="flex items-center gap-10">
                <div className="rounded-[28px] overflow-hidden w-[520px] h-[380px]">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20minimalist%20office%20interior%20with%20clean%20desk%20setup%20contemporary%20workspace%20natural%20lighting%20professional%20business%20environment%20sleek%20furniture%20neutral%20tones%20architectural%20photography%20high%20quality&width=520&height=380&seq=contact-office-interior-001&orientation=landscape"
                    alt="MAX SRL Office"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-[52px] font-serif text-white leading-[1.1]">Get in</span>
                  <span className="text-[52px] font-serif text-white leading-[1.1]">Touch with</span>
                  <span className="text-[52px] font-serif text-white leading-[1.1]">Our Team</span>
                </div>
              </div>
            </div>

            {/* Right Contact Information Grid */}
            <div className="flex-[0_0_45%] pt-8">
              <div className="grid grid-cols-2 gap-x-12 gap-y-10">
                {/* Address */}
                <div className="flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.1em] text-white/50 mb-2 flex items-center gap-2">
                    <i className="ri-map-pin-line text-sm"></i>
                    ADDRESS
                  </span>
                  <span className="text-lg font-light text-white">AGGLOMERATO M ZONA ASI</span>
                </div>

                {/* Country */}
                <div className="flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.1em] text-white/50 mb-2 flex items-center gap-2">
                    <i className="ri-global-line text-sm"></i>
                    COUNTRY
                  </span>
                  <span className="text-lg font-light text-white">Italy</span>
                </div>

                {/* City */}
                <div className="flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.1em] text-white/50 mb-2 flex items-center gap-2">
                    <i className="ri-building-line text-sm"></i>
                    CITY
                  </span>
                  <span className="text-lg font-light text-white">LENTINI</span>
                </div>

                {/* Email */}
                <div className="flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.1em] text-white/50 mb-2 flex items-center gap-2">
                    <i className="ri-mail-line text-sm"></i>
                    EMAIL
                  </span>
                  <a href="mailto:support@max-srl.com" className="text-lg font-light text-white hover:text-white/80 transition-colors cursor-pointer">
                    support@max-srl.com
                  </a>
                </div>

                {/* Zip */}
                <div className="flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.1em] text-white/50 mb-2 flex items-center gap-2">
                    <i className="ri-mail-send-line text-sm"></i>
                    ZIP
                  </span>
                  <span className="text-lg font-light text-white">96016</span>
                </div>

                {/* Number */}
                <div className="flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.1em] text-white/50 mb-2 flex items-center gap-2">
                    <i className="ri-phone-line text-sm"></i>
                    NUMBER
                  </span>
                  <a href="tel:+12566296944" className="text-lg font-light text-white hover:text-white/80 transition-colors cursor-pointer">
                    +1 2566296944
                  </a>
                </div>
              </div>

              {/* Copyright Area */}
              <div className="mt-20 pt-8 border-t border-white/10">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-white/60 font-light">© 2025 MAX SRL</span>
                  <a 
                    href="https://readdy.ai/?ref=logo" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-white/60 font-light hover:text-white/80 transition-colors cursor-pointer"
                  >
                    Powered by Readdy
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
